import { Fragment, type ReactNode } from "react";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { useMediaQuery, useTheme } from "@mui/material";
import { DataTable, type DataTableSelection } from "../DataTable";
import { Facts } from "../Facts";
import { PaginationFooter } from "../PaginationFooter";
import type { ListPagination } from "../../../../lib/pagination";
import { EmptyState } from "../../feedback/EmptyState";

/** How a column participates in the mobile card layout. */
export type DataSequenceCardRole = "primary" | "action" | "hidden";

export interface DataSequenceColumn<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  align?: "left" | "right" | "center";
  /** The column's width in the table, in px or any CSS length; the table only. In the card layout
   *  a column takes the room the card has. Give one to a column of long text. */
  width?: number | string;
  /**
   * How this column appears in the mobile card layout.
   * - primary → the card's heading (title / name), shown without its column title
   * - action  → pinned at the end of the card, vertically centred; convention: use usePopupMenu for multiple actions
   * - hidden  → table only; not rendered in cards
   * Every other column is a line of the card: its column title, then its value.
   */
  cardRole?: DataSequenceCardRole;
}

/** Row-selection wiring. Selectability is a caller decision; selection is table-only. */
export type { DataTableSelection as DataSequenceSelection } from "../DataTable";

interface DataSequenceProps<Row> {
  columns: DataSequenceColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  selection?: DataTableSelection<Row>;
  /** Renders page controls under the list — pass useAsyncPaginatedData's `pagination`. */
  pagination?: ListPagination;
  emptyMessage?: ReactNode;
  /** Navigates or opens a detail view for the clicked row, in both table and card layout. A click
   *  on the selection checkbox or the "action" column does not trigger it. */
  onRowClick?: (row: Row) => void;
}

interface CardRowProps<Row> {
  row: Row;
  primaryCols: DataSequenceColumn<Row>[];
  detailCols: DataSequenceColumn<Row>[];
  actionCol: DataSequenceColumn<Row> | undefined;
  onRowClick?: (row: Row) => void;
}

const CardRowRoot = styled(Stack)<{ $clickable: boolean }>(({ $clickable }) => ({
  justifyContent: "space-between",
  alignItems: "flex-start",
  paddingTop: "12px",
  paddingBottom: "12px",
  paddingLeft: "16px",
  paddingRight: "16px",
  gap: "8px",
  cursor: $clickable ? "pointer" : undefined,
}));

const CardContent = styled(Stack)({
  flex: 1,
  minWidth: 0,
  gap: "8px",
});

const PrimaryLine = styled(Stack)({
  flexWrap: "wrap",
  alignItems: "center",
});

const ActionColumn = styled(Stack)({
  flexShrink: 0,
  alignSelf: "center",
});

function CardRow<Row>({ row, primaryCols, detailCols, actionCol, onRowClick }: CardRowProps<Row>) {
  return (
    <CardRowRoot
      direction="row"
      $clickable={!!onRowClick}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      <CardContent>
        {primaryCols.length > 0 && (
          <PrimaryLine direction="row" gap={1}>
            {primaryCols.map((col) => (
              <Fragment key={col.key}>{col.cell(row)}</Fragment>
            ))}
          </PrimaryLine>
        )}
        {detailCols.length > 0 && (
          <Facts
            items={detailCols.map((col) => ({ label: col.header, value: col.cell(row) }))}
            valueAlign="left"
          />
        )}
      </CardContent>
      {actionCol && (
        <ActionColumn onClick={(e) => e.stopPropagation()}>{actionCol.cell(row)}</ActionColumn>
      )}
    </CardRowRoot>
  );
}

/**
 * Responsive data list: renders a dense table above the md breakpoint and a divider-separated
 * card list below it. Column cardRole controls card layout; see DataSequenceColumn for details.
 * Row selection is supported in table mode only.
 */
export function DataSequence<Row>({
  columns,
  rows,
  getRowId,
  selection,
  emptyMessage = "No items to show.",
  onRowClick,
  pagination,
}: DataSequenceProps<Row>) {
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });

  if (rows.length === 0) return <EmptyState message={emptyMessage} />;

  const primaryCols = columns.filter((c) => c.cardRole === "primary");
  const detailCols = columns.filter((c) => !c.cardRole);
  const actionCol = columns.find((c) => c.cardRole === "action");

  const list = isWide ? (
    <DataTable
      columns={columns}
      rows={rows}
      getRowId={getRowId}
      selection={selection}
      emptyMessage={emptyMessage}
      onRowClick={onRowClick}
    />
  ) : (
    <Stack divider={<Divider />}>
      {rows.map((row) => (
        <CardRow
          key={getRowId(row)}
          row={row}
          primaryCols={primaryCols}
          detailCols={detailCols}
          actionCol={actionCol}
          onRowClick={onRowClick}
        />
      ))}
    </Stack>
  );

  if (!pagination) return list;
  return (
    <>
      {list}
      <PaginationFooter
        count={pagination.total}
        page={pagination.page - 1}
        rowsPerPage={pagination.pageSize}
        rowsPerPageOptions={pagination.pageSizeOptions ?? []}
        onPageChange={(_e, page) => pagination.onPageChange(page + 1)}
        onRowsPerPageChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
      />
    </>
  );
}
